
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Radio,
  Rating,
  CircularProgress
} from '@mui/material';
import { Search, PermIdentity, AccessTime, CalendarToday } from '@mui/icons-material';
import dayjs from 'dayjs';

import { AppDispatch } from '../../../../../../store';
import { fetchWorkerList } from '../../../../../../modules/worker/actions';
import { selectWorkerList } from '../../../../../../modules/worker/selectors';
import { IFacilityShift } from '../../../../../../modules/facilityShifts/types';
import { ActionButton, Container } from './styles';
import { replaceShiftWorker } from '../../../../../../modules/facilityShifts/actions';
import AlertPopup from '../../../../../../components/common/AlertPopup';
import SecureAvatar from 'components/common/SecureAvatar';
import { validateWorkerShiftTiming } from '../../../../../../modules/shift/actions';
import { IWorkerValidationResult } from '../../../../../../modules/shift/types';
import { Dialog, DialogTitle, DialogContent, List, ListItem, Alert, DialogActions, Button } from '@mui/material';

interface ReplaceWorkerProps {
  shift: IFacilityShift;
  currentWorkerId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

const ReplaceWorker: React.FC<ReplaceWorkerProps> = ({ shift, currentWorkerId, onCancel, onSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const workers = useSelector(selectWorkerList);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [validationData, setValidationData] = useState<IWorkerValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [alert, setAlert] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    // Fetch users for this facility
    if (shift.facility) {
      dispatch(fetchWorkerList({
        page: 1,
        pageSize: 10,
        status: 'active',
        facilityId: typeof shift.facility === 'number' ? shift.facility : undefined,
        searchTerm: searchTerm || undefined
      }));
    }
  }, [dispatch, shift.facility, searchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const shiftDuration = dayjs(shift.end_date_time).diff(dayjs(shift.start_date_time), 'hour', true);

  // Filter out the current worker and already assigned workers
  const displayWorkers = (workers || [])
    .filter(w => w.worker_id !== currentWorkerId)
    .filter(w => !(shift as any).assigned_workers_details?.some((aw: any) => aw.id === w.worker_id || aw.worker_id === w.worker_id))
    .slice(0, 5);

  const proceedReplace = async () => {
    setLoading(true);
    if (validationModalOpen) setValidationModalOpen(false);

    try {
      await dispatch(replaceShiftWorker({
        shiftId: shift.shift_id,
        currentWorkerId: currentWorkerId,
        newWorkerId: selectedWorkerId!,
        reason: reason,
      })).unwrap();

      setAlert({
        open: true,
        message: 'Worker replaced successfully!',
        severity: 'success'
      });

      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      setAlert({
        open: true,
        message: err || 'Failed to replace worker',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }

  const handleReplaceClick = async () => {
    if (!shift.shift_id || !selectedWorkerId || !reason) return;

    setIsValidating(true);
    try {
      const response = await dispatch(validateWorkerShiftTiming({
        shift_id: shift.shift_id,
        worker_ids: [selectedWorkerId]
      })).unwrap();

      if (response.status && response.data) {
        const failures = response.data.filter((r: IWorkerValidationResult) => r.validation_failures && r.validation_failures.length > 0);
        if (failures.length > 0) {
          setValidationData(failures);
          setValidationModalOpen(true);
        } else {
          proceedReplace();
        }
      } else {
        proceedReplace();
      }
    } catch (err) {
      console.error("Validation failed", err);
      proceedReplace();
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Container>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>Replace Worker</Typography>

      {/* Shift Summary Header */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarToday sx={{ fontSize: 16, color: '#6B7280' }} />
            <Typography variant="body2" color="textSecondary" fontWeight={500}>
              {dayjs(shift.start_date_time).format('DD/MM/YYYY')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <PermIdentity sx={{ fontSize: 18, color: '#6B7280' }} />
            <Typography variant="body2" color="textSecondary" fontWeight={500}>
              {shift.position_role}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTime sx={{ fontSize: 16, color: '#6B7280' }} />
          <Typography variant="body2" color="textSecondary">
            {dayjs(shift.start_date_time).format('HH.mm')} - {dayjs(shift.end_date_time).format('HH.mm')} • {shiftDuration} hours
          </Typography>
        </Box>
      </Box>

      {/* Available Workers Section */}
      <Typography variant="subtitle1" fontWeight={700} mb={2}>
        Select New Worker
      </Typography>

      <TextField
        fullWidth
        placeholder="Search worker"
        value={searchTerm}
        onChange={handleSearchChange}
        size="small"
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: '#9CA3AF' }} />
            </InputAdornment>
          ),
        }}
      />

      <Box display="flex" flexDirection="column" gap={2} mb={3}>
        {displayWorkers.map((worker) => (
          <Box
            key={worker.worker_id}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setSelectedWorkerId(worker.worker_id)}
            sx={{ cursor: 'pointer', p: 1, borderRadius: 1, '&:hover': { bgcolor: '#F3F4F6' } }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Radio
                checked={selectedWorkerId === worker.worker_id}
                onChange={() => setSelectedWorkerId(worker.worker_id)}
                sx={{ p: 0, '&.Mui-checked': { color: '#52786F' } }}
              />
              <SecureAvatar
                src={worker.user.profile_picture || undefined}
                sx={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={600} color="#111827">
                  {worker.user.first_name} {worker.user.last_name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="textSecondary">
                    {worker.position || 'Worker'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">•</Typography>
                  <Rating value={5} readOnly size="small" max={1} sx={{ fontSize: '14px' }} />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
        {displayWorkers.length === 0 && (
          <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
            No available workers found.
          </Typography>
        )}
      </Box>

      {/* Reason Section */}
      <Typography variant="subtitle2" fontWeight={700} mb={1}>
        Reason for Replacement *
      </Typography>
      <TextField
        fullWidth
        multiline
        rows={2}
        placeholder="Enter reason..."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        size="small"
        sx={{ mb: 3 }}
      />

      {/* Actions */}
      <Box display="flex" flexDirection="column" gap={1.5}>
        <ActionButton
          variant="contained"
          colorType="green"
          fullWidth
          onClick={handleReplaceClick}
          disabled={!selectedWorkerId || !reason || loading || isValidating}
        >
          {loading || isValidating ? <CircularProgress size={24} color="inherit" /> : 'Replace Worker'}
        </ActionButton>
        <ActionButton
          variant="outlined"
          colorType="green"
          fullWidth
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </ActionButton>
      </Box>

      <AlertPopup
        open={alert.open}
        onClose={() => setAlert(prev => ({ ...prev, open: false }))}
        message={alert.message}
        severity={alert.severity}
      />

      <Dialog
        open={validationModalOpen}
        onClose={() => setValidationModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
          Validation Warnings
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            The following workers have validation issues with this shift. Do you still want to assign them?
          </Typography>
          <List>
            {validationData.map((workerResult) => (
              <ListItem key={workerResult.worker_id} sx={{ flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px solid #eee', py: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {workerResult.worker_name}
                </Typography>
                <Box mt={0.5}>
                  {workerResult.validation_failures.map((failure, idx) => (
                    <Alert severity="warning" key={idx} sx={{ py: 0, px: 1, mb: 0.5, '& .MuiAlert-icon': { py: 0.2 } }}>
                      <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                        {failure.message}
                      </Typography>
                    </Alert>
                  ))}
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationModalOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={proceedReplace} color="error" variant="contained">
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReplaceWorker;
